import React, {useRef} from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import {object, string} from 'yup';
import Validator from 'card-validator';
import {Formik, Form, Field as FieldBase} from 'formik';

// Import Assets
import {
    Slash,
    IconError,
    CardError,
    DotsLoader,
    Success,
    CardLock,
    CardMasterCard,
    CardTemplate,
    CardVISA
} from './assets';

import './styles.module.css';
import './fonts/Inter/inter.css';
import './styles/reset.css';
import './styles/index.scss';
import {formatPrice} from '../../../helpers/Helper';
// Import Assets [END]

/** Regex: Allows for 01-12 entries */
const validMonth = /^1[0-2]$|^0[1-9]$/;
/** Regex: Allows for 00-99 entries; secondary validation provided later */
const validYear = /^[0-9]{2}$/;

export const transactionStates = {
    ERROR: 'error',
    DEFAULT: 'default',
    LOADING: 'loading',
    SUCCESS: 'success'
};

/**
 * Initializers for form state
 */
const defaultFormValues = {
    cardNumber: '',
    expiryMonth: '',
    expiryYear: '',
    cardCVC: ''
};

const defaultFormStatus = {transactionState: transactionStates.DEFAULT};

/**
 * Schema using the Yup library for more readable field object validation
 */
const ccFormSchema = object()
    .shape({
        cardNumber: string()
            .required('Please enter your card number')
            .matches(/\d+/g)
            .min(13, 'Invalid credit card number')
            .max(16, 'Invalid credit card number'),
        expiryMonth: string()
            .required('Please enter your card\'s expiry month')
            .length(2, 'Please match the MM format')
            .test('validMonth', 'Valid months are from 01-12', value => validMonth.test(value)),
        expiryYear: string()
            .required('Please enter your card\'s expiry year')
            .length(2, 'Only the last two digits are required')
            .test('validYear', 'Only the last two digits are required', value => validYear.test(value)),
        cardCVC: string()
            .required('Please enter your card\'s CVC/CVV code')
    });

// eslint-disable-next-line react/jsx-props-no-spreading
const Field = React.forwardRef((props, ref) => <FieldBase innerRef={ref} {...props} />);

export const PaymentGateway = ({layout, price, onFormSubmit}) => {
    // ref is necessary to prevent re-render bug: text getting removed
    const cardType = useRef('');
    const cardNumber = useRef();
    const expiryMonth = useRef();
    const expiryYear = useRef();
    const cardCVC = useRef();
    const submitRef = useRef();
    const cardRefs = [cardNumber, expiryMonth, expiryYear, cardCVC, submitRef];

    /**
     * Handles the submission of the form
     * @param {defaultFormValues} values
     */
    async function handleFormSubmit(values, {setStatus, setSubmitting, setFieldError}) {
        const originalCardType = cardType.current;
        const setTransactionStatus = status => setStatus({transactionState: status});
        const setError = error => {
            setTransactionStatus(transactionStates.ERROR);
            setFieldError('cardNumber', error);
        };
        const setFormSubmitting = isSubmitting => {
            if (isSubmitting) {
                setTransactionStatus(transactionStates.LOADING);
                cardType.current = 'locked';
            } else cardType.current = originalCardType;
        };
        return onFormSubmit(values, {
            setTransactionStatus,
            setFormSubmitting,
            setError
        });
    }

    function handleCCValidation(value) {
        let error;
        const numberValidation = Validator.number(value);
        if (!numberValidation.isPotentiallyValid) {
            error = 'Card number is incomplete/invalid';
            cardType.current = '';
        }

        //Ref is necessary to prevent re-render bug: text getting removed
        if (numberValidation.card) cardType.current = numberValidation.card.type;
        else cardType.current = '';

        return error;
    }

    function handleCVCValidation(value) {
        // American express CVV codes are 4 digits in length
        const validCVC = cardType.current === 'american-express' ?
            /[0-9]{3}[1-9]/g :
            /[0-9]{2}[1-9]/g;
        let error;
        if (!validCVC.test(value)) {
            error = 'Invalid Format for the CVC/CVV code';
        }

        return error;
    }

    function handleExpiryYearValidation(value) {
        const currYear = new Date().getFullYear();
        const entryYear = '20'.concat(value);
        let error;
        if (entryYear < currYear) {
            error = 'This card is expired';
        } else if (!validYear.test(value)) {
            error = 'Invalid expiry year. Please match the \'YY\' format';
        }

        return error;
    }

    function shouldDisableSubmit(values, touched, errors) {
        let enabled = true;
        if (touched.constructor === Object &&
            Object.entries(errors).length === 0 &&
            Object.entries(touched).length > 0 &&
            values.cardNumber.length > 0 &&
            values.expiryMonth.length > 0 &&
            values.expiryYear.length > 0 &&
            values.cardCVC.length > 2) {
            enabled = false;
        }
        return enabled;
    }

    /**
     * Allows only numbers in input fields & moves to next input when maxlength is met
     * @param {Event} e
     * @param {Function} handleChange
     */
    function handleFieldInputs(e, handleChange) {
        const onlyNumbersRegExp = /^[0-9]*$/;
        const {name, value} = e.target;

        if (value !== '' && !onlyNumbersRegExp.test(value)) {
            return false;
        }

        if (value.length >= e.target.getAttribute('maxlength')) {
            const indexOfRef = cardRefs.findIndex(el => el.current.name === name);
            cardRefs[indexOfRef + 1].current.focus();
        }

        handleChange(name)(value);
    }

    /** Styled Components */
    const CardWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto;
    height: 100%;
    width: 100%;
    padding: 0 var(--space-32) 0 var(--space-32);

    .locked {
        background-color: var(--color-neutral-gray-300);
        box-shadow: none;
    }

    & > form {
        grid-gap: var(--space-8);
        display: grid;
        width: 100%;
    }
  `;

    const CardInput = styled.div`
    display: grid;
    grid-template-columns: 28px 1fr auto 50px;
    grid-gap: var(--space-8);
    grid-auto-flow: row;
    align-items: center;
    justify-content: center;
    width: 100%;
    padding: var(--space-4) var(--space-10);
    border: 1px solid var(--color-neutral-gray-400);

    border-radius: var(--space-6);
    background-color: var(--color-white);
    box-shadow: var(--shadow-inset);
    & > img {
      user-select: none;
      pointer-events: none;
    }
    .input-field {
      height: var(--space-32);
      opacity: 1;

      &:hover::placeholder {
        color: var(--color-neutral-gray-600);
        transition: opacity 0.2s ease-in;
      }
      &:focus::placeholder {
        opacity: 0;
        transition: opacity 0.2s ease-in;
      }
    }
    & > input {
      border: none;
      width: 100%;

      font-size: var(--font-16);
      &::placeholder {
        color: var(--color-neutral-gray-400);
      }
      /* CVV */
      &:nth-child(4) {
        min-width: var(--space-40);

        text-align: center;
      }
      &:focus {
        outline: none;
        box-shadow: none;
        border: none;
      }
      &:active {
        outline: none;
        box-shadow: none;
        border: none;
      }
    }
  `;

    const PayButton = styled.button`
    display: flex;
    flex-grow: 1;
    width: 100%;
    align-items: center;
    justify-content: center;
    padding: var(--space-10);
    border: none;

    border-radius: var(--space-6);
    color: var(--color-white);
    text-align: center;
    font-size: var(--font-16);
    font-weight: 700;
    line-height: var(--space-20);
    background-color: ${props => (props.success ? 'var(--color-green-500)' : props.isLoading ? 'var(--color-blue-500)' : props.disabled ? 'var(--color-neutral-gray-500)' : 'var(--color-neutral-gray-500)')};
    box-shadow: ${props => (props.disabled ? 'var(--shadow-blank)' : 'var(--shadow-md)')};
    &:hover {
      background-color: ${props => (props.success ? 'var(--color-green-600)' : props.isLoading ? 'var(--color-blue-600)' : props.disabled ? 'var(--color-neutral-gray-500)' : 'var(--color-neutral-gray-500)')};
      transition: background-color 0.3s ease-in;
    }
  `;

    const Errors = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    height: var(--space-20);
    width: 100%;

    color: var(--color-red-700);
    text-align: left;

    div {
      display: flex;
      align-items: center;
      justify-content: center;
      & > img {
        margin-right: var(--space-4);
      }
    }
  `;

    const Expiration = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    & > input {
      width: var(--space-40);
      border: none;
      &::placeholder {
        color: var(--color-neutral-gray-400);
      }
      font-size: var(--font-16);
      /* MM */
      &:nth-child(1) {
        width: var(--space-32);

        text-align: center;
        &::placeholder {
          text-align: left;
        }
      }
      /* YY */
      &:nth-child(3) {
        padding-left: var(--space-4);
        width: var(--space-28);

        text-align: center;
        &::placeholder {
          text-align: left;
        }
      }
      &:focus {
        outline: none;
        box-shadow: none;
        border: none;
      }
    }
  `;

    return <CardWrapper>
        <Formik
            initialStatus={defaultFormStatus}
            initialValues={defaultFormValues}
            validationSchema={ccFormSchema}
            onSubmit={handleFormSubmit}
        >
            {({isSubmitting, errors, touched, values, status, handleChange}) => (
                <Form>
                    <CardInput
                        className={(isSubmitting || status.transactionState === transactionStates.LOADING || status.transactionState === transactionStates.SUCCESS ? 'locked' : '')}
                    >
                        <Card type={cardType.current}/>
                        <Field
                            type="text"
                            name="cardNumber"
                            validate={handleCCValidation}
                            placeholder="Card Number"
                            maxLength={16}
                            className={`input-field ${(isSubmitting || status.transactionState === transactionStates.LOADING || status.transactionState === transactionStates.SUCCESS ? 'locked' : '')}`}
                            disabled={isSubmitting || status.transactionState === transactionStates.LOADING || status.transactionState === transactionStates.SUCCESS}
                            onChange={e => handleFieldInputs(e, handleChange)}
                            ref={cardNumber}
                            tabIndex="0"
                        />
                        <Expiration>
                            <Field
                                type="text"
                                name="expiryMonth"
                                placeholder="MM"
                                maxLength={2}
                                className={`input-field ${(isSubmitting || status.transactionState === transactionStates.LOADING || status.transactionState === transactionStates.SUCCESS ? 'locked' : '')}`}
                                disabled={isSubmitting || status.transactionState === transactionStates.LOADING || status.transactionState === transactionStates.SUCCESS}
                                onChange={e => handleFieldInputs(e, handleChange)}
                                ref={expiryMonth}
                                tabIndex="0"
                            />
                            <img src={Slash} alt="Slash"/>
                            <Field
                                type="text"
                                name="expiryYear"
                                validate={handleExpiryYearValidation}
                                placeholder="YY"
                                maxLength={2}
                                className={`input-field ${(isSubmitting || status.transactionState === transactionStates.LOADING || status.transactionState === transactionStates.SUCCESS ? 'locked' : '')}`}
                                disabled={isSubmitting || status.transactionState === transactionStates.LOADING || status.transactionState === transactionStates.SUCCESS}
                                onChange={e => handleFieldInputs(e, handleChange)}
                                ref={expiryYear}
                                tabIndex="0"
                            />
                        </Expiration>
                        <Field
                            type="text"
                            name="cardCVC"
                            validate={handleCVCValidation}
                            placeholder="CVV"
                            maxLength={cardType.current === 'american-express' ? 4 : 3}
                            className={`input-field ${(isSubmitting || status.transactionState === transactionStates.LOADING || status.transactionState === transactionStates.SUCCESS ? 'locked' : '')}`}
                            disabled={isSubmitting || status.transactionState === transactionStates.LOADING || status.transactionState === transactionStates.SUCCESS}
                            onChange={e => handleFieldInputs(e, handleChange)}
                            ref={cardCVC}
                            tabIndex="0"
                        />
                    </CardInput>
                    <PayButton
                        type="submit"
                        disabled={shouldDisableSubmit(values, touched, errors) || isSubmitting || status.transactionState === transactionStates.LOADING || status.transactionState === transactionStates.SUCCESS}
                        isLoading={status.transactionState === transactionStates.LOADING || !shouldDisableSubmit(values, touched, errors)}
                        success={status.transactionState === transactionStates.SUCCESS}
                        ref={submitRef}
                        tabIndex="0"
                    >
                        {(status.transactionState === transactionStates.DEFAULT || status.transactionState === transactionStates.ERROR) && `Pay $${formatPrice(price, false, false)}`}
                        {status.transactionState === transactionStates.LOADING && <DotsLoader/>}
                        {status.transactionState === transactionStates.SUCCESS && <Success/>}
                    </PayButton>
                    <Errors>
                        <ErrorDisplay errors={errors} touched={touched}/>
                    </Errors>
                </Form>
            )}
        </Formik>
    </CardWrapper>;
};

function ErrorDisplay({errors, touched}) {
    let error = '';

    if (errors.cardNumber && touched.cardNumber) {
        error = errors.cardNumber;
    } else if (errors.expiryMonth && touched.expiryMonth) {
        error = errors.expiryMonth;
    } else if (errors.expiryYear && touched.expiryYear) {
        error = errors.expiryYear;
    } else if (errors.cardCVC && touched.cardCVC) {
        error = errors.cardCVC;
    } else {
        return null;
    }

    return (
        <div>
            <img src={IconError} alt="Error Icon"/>
            <span>{error}</span>
        </div>
    );
}

function Card({type}) {
    let source = CardTemplate;
    let altName = '';
    if (type === '') {
        source = CardTemplate;
    } else if (type === 'visa') {
        source = CardVISA;
        altName = 'Visa Card';
    } else if (type === 'mastercard') {
        source = CardMasterCard;
        altName = 'MasterCard';
    } else if (type === 'locked') {
        source = CardLock;
        altName = 'Locked Transaction';
    } else {
        source = CardError;
        altName = 'Unrecognized Card';
    }

    return <img src={source} alt={altName}/>;
}

Card.propTypes = {type: PropTypes.string.isRequired};
ErrorDisplay.propTypes = {
    errors: PropTypes.object.isRequired,
    touched: PropTypes.object.isRequired
};
PaymentGateway.propTypes = {
    layout: PropTypes.string,
    onFormSubmit: PropTypes.func,
    price: PropTypes.number.isRequired
};
